import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { Box } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import type { Nurse } from "../../../../utils/models";
import type { GridDates, HiringGridCoord, HiringRecommendation } from "../types";
import { recommendationKey } from "../utils/moveRequests";
import MoveOpportunityCard from "./MoveOpportunityCard";
import OpportunitySection from "./OpportunitySection";
import { SETTINGS_TRIGGER_SX } from "./ShiftScopeChips";
import ShiftScopePopover from "./ShiftScopePopover";

/** One md-breakpoint screen of the two-column grid. Each card owns a query, so this bounds the fetches. */
const CARD_BATCH = 8;

/** One identity for "nothing reported yet", so an untouched render does not churn the memo below. */
const NO_KEYS: ReadonlySet<string> = new Set();

type Props = {
	/** Already filtered to `move` recommendations, worst first. */
	recommendations: HiringRecommendation[];
	weekNumbers: number[];
	/** Passed straight through to the cards, which name the calendar day of each end of a move. */
	dates: GridDates;
	/** Only this panel needs it threaded in — every other hire surface already receives it. */
	weekStartDay: number;
	jobDisplayName: string;
	/** The page's shift filter, surfaced here too — this board is scoped by it like the grid is. */
	shiftLabels: string[];
	enabledShiftLabels: string[];
	onToggleShift: (label: string) => void;
	requested: readonly string[];
	/** Nothing asked yet → Revert has nothing to undo and stays disabled. */
	canRevert: boolean;
	onRevert: () => void;
	onHoverChange: (coords: HiringGridCoord[]) => void;
	onRequestMove: (rec: HiringRecommendation, nurseId: number) => void;
	onUnrequestMove: (rec: HiringRecommendation, nurseId: number) => void;
	getNurseById: (id: number) => Nurse | undefined;
};

/** The Move tab: overstaffed shifts paired with understaffed ones, with the people who could cover each. */
const MoveOpportunitiesPanel: FC<Props> = ({
	recommendations,
	weekNumbers,
	dates,
	weekStartDay,
	jobDisplayName,
	shiftLabels,
	enabledShiftLabels,
	onToggleShift,
	requested,
	canRevert,
	onRevert,
	onHoverChange,
	onRequestMove,
	onUnrequestMove,
	getNurseById,
}) => {
	const { t } = useTranslation();
	const [visibleCount, setVisibleCount] = useState(CARD_BATCH);

	/**
	 * Moves whose eligible-staff list came back empty. Their cards render nothing, so counting raw
	 * recommendations put a header and an empty grid where the "no moves available" state belongs — and
	 * a section of three uncoverable moves looked like a rendering failure.
	 *
	 * Reported by the cards because only they hold the query. The set is cleared whenever the
	 * recommendations themselves change, so a key can never outlive the move it describes.
	 */
	const keySignature = recommendations.map(recommendationKey).join("|");
	/**
	 * Held WITH the recommendation list it describes, and compared during render rather than reset in an
	 * effect: an effect would paint one frame with the previous list's answers before correcting itself,
	 * and it is the documented way to adjust state when props change. The signature is the list flattened
	 * to a primitive, so a refetch returning the same moves in a new array does not discard what we know.
	 */
	const [empty, setEmpty] = useState<{ signature: string; keys: ReadonlySet<string> }>({ signature: keySignature, keys: NO_KEYS });
	const emptyKeys = empty.signature === keySignature ? empty.keys : NO_KEYS;

	const handleNoEligibleStaff = useCallback(
		(recKey: string) =>
			setEmpty((prev) => {
				const keys = prev.signature === keySignature ? prev.keys : NO_KEYS;
				return keys.has(recKey) ? { signature: keySignature, keys } : { signature: keySignature, keys: new Set(keys).add(recKey) };
			}),
		[keySignature],
	);

	// Dropping an uncoverable move also pulls the next one into the batch, so "show 4" stays 4 real cards.
	const coverable = useMemo(() => recommendations.filter((rec) => !emptyKeys.has(recommendationKey(rec))), [recommendations, emptyKeys]);
	const visible = coverable.slice(0, visibleCount);

	return (
		<OpportunitySection
			icon={<SwapHorizIcon />}
			title={t("hiring.move.sectionTitle")}
			subtitle={
				// The department catalog can be missing the job's display name — the sentence still has to
				// read, so it drops the role rather than leaving a gap in the middle of it.
				jobDisplayName ? t("hiring.move.sectionSubtitle", { role: jobDisplayName }) : t("hiring.move.sectionSubtitleNoRole")
			}
			headerActions={
				// Same cluster shape as the Suggested Hires header: a flex row, so the two controls sit
				// side by side rather than stacking in `PanelHeader`'s ending slot.
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
					<SystemButton
						buttonVariant="secondary"
						size="compact"
						sx={SETTINGS_TRIGGER_SX}
						noFullWidthMobile
						disabled={!canRevert}
						leadingIcon={<RotateLeftIcon sx={{ fontSize: 14 }} />}
						leadingIconGap="6px"
						onClick={onRevert}
						tooltip={t("hiring.move.revertTooltip")}
						data-testid="hiring-move-revert"
					>
						{t("hiring.hire.revert")}
					</SystemButton>
					{shiftLabels.length > 1 && <ShiftScopePopover shiftLabels={shiftLabels} enabledShiftLabels={enabledShiftLabels} onToggleShift={onToggleShift} />}
				</Box>
			}
			// The COVERABLE count, not the raw one: a move nobody can cover renders no card, and the section
			// keys its empty state on this number.
			totalCount={coverable.length}
			visibleCount={visible.length}
			onShowMore={() => setVisibleCount((count) => count + CARD_BATCH)}
			emptyIcon={<TaskAltOutlinedIcon />}
			emptyTitle={t("hiring.move.emptyTitle")}
			emptyHint={t("hiring.move.emptyHint")}
			dataTestId="hiring-move-opportunities"
		>
			{visible.map((rec) => {
				const key = recommendationKey(rec);
				return (
					<MoveOpportunityCard
						key={key}
						rec={rec}
						recKey={key}
						weekNumbers={weekNumbers}
						dates={dates}
						weekStartDay={weekStartDay}
						jobDisplayName={jobDisplayName}
						requested={requested}
						onHoverChange={onHoverChange}
						onRequestMove={onRequestMove}
						onUnrequestMove={onUnrequestMove}
						onNoEligibleStaff={handleNoEligibleStaff}
						getNurseById={getNurseById}
					/>
				);
			})}
		</OpportunitySection>
	);
};

export default MoveOpportunitiesPanel;
